import React, { useEffect, useRef, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { siWhatsapp } from "simple-icons";
import {
  Archive,
  ArchiveRestore,
  ArrowLeft,
  Check,
  CircleDot,
  Download,
  ExternalLink,
  Info,
  MessageSquare,
  RefreshCw,
  Trash2,
} from "lucide-react";
import {
  useBot,
  useConversation,
  useConversations,
  useConversationStats,
  useDeleteConversation,
  useSetConversationStatus,
} from "../hooks/useApi";
import { api } from "../api/client";
import { useToast } from "@/hooks/use-toast";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import { BrandCard, BrandStyles, HandNote, VARIABILI_SEZIONE } from "../components/integrations/brand";
import {
  BOTTONE,
  GRIGIO,
  SCURO,
  IntestazioneCasella,
  Interruttore,
  NessunaScelta,
  Paginazione,
  Ricerca,
  raggruppa,
  schermoLargo,
} from "../components/integrations/casella";
import { gruppoDi, paginaLeggibile, quandoBreve, quandoEsteso } from "../components/contacts/recapiti";

const PER_PAGINA = 50;
// Quanto aspettare dopo l'ultima lettera prima di cercare.
const ATTESA_RICERCA = 250;

// Le categorie dell'analisi, come le legge chi gestisce il bot.
const ARGOMENTI = {
  product: "Prodotti",
  support: "Assistenza",
  shipping: "Spedizioni",
  pricing: "Prezzi",
  appointment: "Appuntamenti",
  business: "Azienda",
  company: "Azienda",
  technical: "Tecnico",
  general: "Generale",
  other: "Altro",
};

const isWhatsApp = (conv) => conv?.metadata?.channel === "whatsapp" || conv?.metadata?.source === "whatsapp_pingup";

/** Chi e': il nome dal contatto lasciato, o da WhatsApp; altrimenti un visitatore. */
function chiE(conv) {
  const contatto = conv?.contact || conv?.contacts?.at(-1);
  return contatto?.headline || conv?.metadata?.user_context?.name || conv?.metadata?.user_context?.phone || "";
}

/** Le risposte salvate possono contenere i segnaposto delle fonti ({{1,2}}): non sono per chi legge. */
const pulisci = (testo) => String(testo || "").replace(/\s*\{\{[^}]*\}\}/g, "").trim();

/**
 * Le chat del bot, come una casella: a sinistra l'elenco (per giorno, con la
 * prima domanda e i segni dell'analisi), a destra la conversazione con cosa
 * ne e' nato: un contatto, un passaggio a WhatsApp. Stessa struttura della
 * pagina Contatti, e come i contatti si leggono e si archiviano: aprirne una
 * la segna letta, un messaggio nuovo la rimette da leggere (e in arrivo, se
 * era archiviata). La conversazione aperta sta nell'indirizzo (?sessione=),
 * cosi' "Leggi la conversazione" di un contatto apre proprio lei.
 */
export default function ConversationsPage() {
  const { id: botId } = useParams();
  // Passando da un bot all'altro la chiave rimonta la pagina.
  return <CasellaConversazioni key={botId} botId={botId} />;
}

function CasellaConversazioni({ botId }) {
  const { toast } = useToast();
  const [params, setParams] = useSearchParams();
  const selezionataId = params.get("sessione");

  // In arrivo, con un contatto (fra quelle in arrivo) o archiviate.
  const [vista, setVista] = useState("open");
  const [pagina, setPagina] = useState(1);
  const [cerca, setCerca] = useState("");
  const [q, setQ] = useState("");
  const [daCancellare, setDaCancellare] = useState(null);
  const [dettagli, setDettagli] = useState(false);

  useEffect(() => {
    const attesa = setTimeout(() => {
      setQ(cerca.trim());
      setPagina(1);
    }, ATTESA_RICERCA);
    return () => clearTimeout(attesa);
  }, [cerca]);

  const { data: bot } = useBot(botId);
  const { data: stats } = useConversationStats(botId);
  const {
    data: elenco,
    isLoading,
    isFetching,
    error,
    refetch,
  } = useConversations(botId, {
    page: pagina,
    limit: PER_PAGINA,
    q,
    status: vista === "archived" ? "archived" : "open",
    conContatto: vista === "contatto",
  });
  const conversazioni = elenco?.conversations || [];
  const { data: aperta, isLoading: apertaInCaricamento } = useConversation(botId, selezionataId);
  const cancella = useDeleteConversation();
  const cambiaStato = useSetConversationStatus(botId);

  // Aprire una conversazione vuol dire averla letta: il pallino dei nuovi
  // resta vero senza doverci pensare.
  useEffect(() => {
    if (aperta?.status === "new") {
      cambiaStato.mutate({ sessionId: aperta.session_id, status: "read" });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [aperta?.session_id]);

  // Sul telefono la conversazione prende il posto dell'elenco: si riparte
  // dalla sua cima, anche se l'elenco era scorso in basso.
  const dettaglio = useRef(null);
  useEffect(() => {
    if (selezionataId && !schermoLargo()) dettaglio.current?.scrollIntoView?.({ block: "start" });
  }, [selezionataId]);

  const scegli = (id) => {
    const prossimi = new URLSearchParams(params);
    if (id) prossimi.set("sessione", id);
    else prossimi.delete("sessione");
    // Dall'elenco alla conversazione si aggiunge un passo alla cronologia:
    // sul telefono "indietro" torna all'elenco. Tra una e l'altra no.
    setParams(prossimi, { replace: Boolean(selezionataId && id) });
  };

  // Dopo archivia o cancella, su schermo largo si passa alla conversazione
  // dopo, come nella posta; sul telefono si torna all'elenco.
  const vicina = (id) => {
    const i = conversazioni.findIndex((c) => c.session_id === id);
    if (!schermoLargo() || i < 0) return null;
    return conversazioni[i + 1]?.session_id || conversazioni[i - 1]?.session_id || null;
  };

  // avanti: alla conversazione dopo; chiudi: di nuovo all'elenco.
  const imposta = async (conv, status, { avanti = false, chiudi = false } = {}) => {
    const dopo = avanti ? vicina(conv.session_id) : chiudi ? null : undefined;
    try {
      await cambiaStato.mutateAsync({ sessionId: conv.session_id, status });
      if (dopo !== undefined) scegli(dopo);
    } catch (err) {
      toast({ title: "Modifica non riuscita", description: err?.message || "Riprova fra poco.", variant: "destructive" });
    }
  };

  const confermaCancellazione = async () => {
    const dopo = vicina(daCancellare);
    try {
      await cancella.mutateAsync({ botId, sessionId: daCancellare });
      setDaCancellare(null);
      scegli(dopo);
      toast({ title: "Conversazione cancellata" });
    } catch (err) {
      toast({ title: "Cancellazione non riuscita", description: err?.message || "Riprova fra poco.", variant: "destructive" });
    }
  };

  const esporta = async () => {
    try {
      const blob = await api.conversations.exportCsv(botId);
      const url = URL.createObjectURL(blob instanceof Blob ? blob : new Blob([blob]));
      const link = document.createElement("a");
      link.href = url;
      link.download = `conversazioni-${(bot?.name || "bot").replace(/[^a-z0-9]+/gi, "-").toLowerCase()}.csv`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      toast({ title: "Esportazione non riuscita", description: err?.message || "Riprova fra poco.", variant: "destructive" });
    }
  };

  const cambiaVista = (nuova) => {
    setVista(nuova);
    setPagina(1);
  };

  const totale = stats?.total_conversations;
  const nessuna = totale === 0;
  const inbox = stats?.inbox;

  return (
    <div className="relative isolate" style={VARIABILI_SEZIONE}>
      <BrandStyles />

      <IntestazioneCasella
        icona={MessageSquare}
        titolo="Conversazioni"
        dettagli={totale ? <Conteggi stats={stats} /> : null}
        azioni={
          totale ? (
            <button type="button" onClick={esporta} className={GRIGIO}>
              <Download className="h-3.5 w-3.5" aria-hidden="true" />
              Esporta CSV
            </button>
          ) : null
        }
      />

      <div className="pu-fade-up mx-auto max-w-[1400px] pb-10">
        {nessuna ? (
          <Benvenuto botId={botId} />
        ) : (
          <>
            {/* Sul telefono, con una conversazione aperta, conta solo lei. */}
            <div
              className={`${selezionataId ? "hidden lg:flex" : "flex"} flex-col gap-3 sm:flex-row sm:items-center sm:justify-between`}
            >
              {/* Sui telefoni stretti le tre voci scorrono di lato invece di uscire. */}
              <div className="-mx-1 max-w-full overflow-x-auto px-1">
                <Interruttore
                  etichetta="Quali conversazioni"
                  valore={vista}
                  onCambia={cambiaVista}
                  voci={[
                    { valore: "open", etichetta: "In arrivo", numero: inbox ? inbox.new + inbox.read : null },
                    { valore: "contatto", etichetta: "Con un contatto" },
                    { valore: "archived", etichetta: "Archiviate", numero: inbox ? inbox.archived : null },
                  ]}
                />
              </div>
              <div className="flex gap-2">
                <Ricerca
                  valore={cerca}
                  onCambia={setCerca}
                  segnaposto="Cerca nelle conversazioni"
                  etichetta="Cerca nelle conversazioni"
                />
                <button
                  type="button"
                  onClick={() => refetch()}
                  aria-label="Aggiorna l'elenco"
                  title="Aggiorna l'elenco"
                  className={`${GRIGIO} h-10 w-10 shrink-0 px-0`}
                >
                  <RefreshCw className={`h-4 w-4 ${isFetching ? "animate-spin" : ""}`} aria-hidden="true" />
                </button>
              </div>
            </div>

            <div className="mt-4 grid items-start gap-4 lg:grid-cols-[minmax(320px,400px)_minmax(0,1fr)]">
              <div className={selezionataId ? "hidden lg:block" : ""}>
                {error ? (
                  <BrandCard className="space-y-3 p-6 text-sm text-[#64748B]">
                    <p>Non riesco a caricare le conversazioni.</p>
                    <button type="button" className={GRIGIO} onClick={() => refetch()}>
                      Riprova
                    </button>
                  </BrandCard>
                ) : (
                  <Elenco
                    conversazioni={conversazioni}
                    caricamento={isLoading}
                    selezionataId={selezionataId}
                    onScegli={scegli}
                    paginazione={elenco?.pagination}
                    onPagina={setPagina}
                    vuoto={
                      <ElencoVuoto vista={vista} q={q} inbox={inbox} onAzzeraRicerca={() => setCerca("")} />
                    }
                  />
                )}
              </div>
              <div ref={dettaglio} className={`scroll-mt-4 lg:sticky lg:top-4 ${selezionataId ? "" : "hidden lg:block"}`}>
                {aperta ? (
                  <Conversazione
                    key={aperta.session_id}
                    conv={aperta}
                    botId={botId}
                    inCorso={cambiaStato.isPending}
                    onIndietro={() => scegli(null)}
                    onDettagli={() => setDettagli(true)}
                    onArchivia={() => imposta(aperta, "archived", { avanti: true })}
                    onRipristina={() => imposta(aperta, "new")}
                    // Riaprirla la segnerebbe di nuovo letta: si torna all'elenco.
                    onDaLeggere={() => imposta(aperta, "new", { chiudi: true })}
                    onCancella={() => setDaCancellare(aperta.session_id)}
                  />
                ) : selezionataId && apertaInCaricamento ? (
                  <BrandCard className="space-y-4 p-6">
                    <Skeleton className="h-10 w-2/3" />
                    <Skeleton className="h-16 w-3/4" />
                    <Skeleton className="ml-auto h-12 w-1/2" />
                  </BrandCard>
                ) : selezionataId ? (
                  <BrandCard className="p-6 text-sm text-[#64748B]">
                    Questa conversazione non c'è più: forse è stata cancellata.{" "}
                    <button type="button" className="font-medium text-[#18181B] underline" onClick={() => scegli(null)}>
                      Torna all'elenco
                    </button>
                  </BrandCard>
                ) : (
                  <NessunaScelta testo="Scegli una conversazione dall'elenco" />
                )}
              </div>
            </div>
          </>
        )}
      </div>

      <Dialog open={Boolean(daCancellare)} onOpenChange={(open) => !open && setDaCancellare(null)}>
        <DialogContent className="max-w-md rounded-[14px]">
          <DialogHeader>
            <DialogTitle>Cancellare questa conversazione?</DialogTitle>
            <DialogDescription>
              La chat viene rimossa per sempre. Se il visitatore ha lasciato un contatto, quello resta tra i Contatti.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2">
            <button type="button" className={GRIGIO} onClick={() => setDaCancellare(null)}>
              Annulla
            </button>
            <button
              type="button"
              className={`${BOTTONE} bg-[#DC2626] text-white hover:bg-[#B91C1C]`}
              onClick={confermaCancellazione}
              disabled={cancella.isPending}
            >
              {cancella.isPending ? "Cancellazione…" : "Cancella per sempre"}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={dettagli && Boolean(aperta)} onOpenChange={setDettagli}>
        <DialogContent className="max-w-lg rounded-[14px]">
          <DialogHeader>
            <DialogTitle>Dettagli tecnici</DialogTitle>
            <DialogDescription>Servono di rado: per esempio per ritrovare una chat segnalata da un cliente.</DialogDescription>
          </DialogHeader>
          {aperta ? <DettagliTecnici conv={aperta} /> : null}
        </DialogContent>
      </Dialog>
    </div>
  );
}

/** Sotto il titolo: quante da leggere, in arrivo e archiviate, e quante domande. */
function Conteggi({ stats }) {
  const domande = stats.total_messages || 0;
  const inbox = stats.inbox;
  return (
    <p className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-[#475569]">
      {inbox ? (
        <>
          {inbox.new ? (
            <span className="inline-flex items-center gap-1.5">
              <span className="h-2 w-2 rounded-full bg-[#A855F7]" aria-hidden="true" />
              <strong className="font-semibold text-[#020617]">{inbox.new}</strong> da leggere
            </span>
          ) : (
            <span className="inline-flex items-center gap-1.5">
              <Check className="h-3.5 w-3.5 text-[#0F7A3D]" aria-hidden="true" />
              Tutte lette
            </span>
          )}
          <span className="text-[#CBD5E1]" aria-hidden="true">·</span>
          <span>
            <strong className="font-semibold text-[#020617]">{inbox.new + inbox.read}</strong> in arrivo
          </span>
          {inbox.archived ? (
            <>
              <span className="text-[#CBD5E1]" aria-hidden="true">·</span>
              <span>
                <strong className="font-semibold text-[#020617]">{inbox.archived}</strong>{" "}
                {inbox.archived === 1 ? "archiviata" : "archiviate"}
              </span>
            </>
          ) : null}
        </>
      ) : (
        <span>
          <strong className="font-semibold text-[#020617]">{stats.total_conversations}</strong>{" "}
          {stats.total_conversations === 1 ? "conversazione" : "conversazioni"}
        </span>
      )}
      {domande ? (
        <>
          <span className="text-[#CBD5E1]" aria-hidden="true">·</span>
          <span>
            <strong className="font-semibold text-[#020617]">{domande}</strong> {domande === 1 ? "domanda" : "domande"} dei visitatori
          </span>
        </>
      ) : null}
    </p>
  );
}

/** Il tondo accanto alla conversazione: iniziali se si sa chi e', il logo per WhatsApp, se no un fumetto. */
function Tondo({ conv, grande = false }) {
  const dimensione = grande ? "h-12 w-12" : "h-9 w-9";
  if (isWhatsApp(conv)) {
    return (
      <span className={`flex shrink-0 items-center justify-center rounded-full bg-[#25D366]/10 ${dimensione}`} aria-hidden="true">
        <svg viewBox="0 0 24 24" className={grande ? "h-6 w-6" : "h-4 w-4"} fill="#25D366">
          <path d={siWhatsapp.path} />
        </svg>
      </span>
    );
  }
  const nome = chiE(conv);
  const lettere = nome
    .split(/\s+/)
    .filter((p) => /\p{L}/u.test(p))
    .slice(0, 2)
    .map((p) => p.match(/\p{L}/u)[0].toUpperCase())
    .join("");
  return (
    <span
      className={`flex shrink-0 items-center justify-center rounded-full bg-[#F1F5F9] font-semibold text-[#334155] ${dimensione} ${
        grande ? "text-base" : "text-xs"
      }`}
      aria-hidden="true"
    >
      {lettere || <MessageSquare className={grande ? "h-5 w-5 text-[#94A3B8]" : "h-4 w-4 text-[#94A3B8]"} />}
    </span>
  );
}

/**
 * I segni dell'analisi, sobri: un pallino colorato e una parola. Il titolo
 * spiega cosa vuol dire a chi passa sopra.
 */
function Segni({ conv }) {
  const a = conv.analysis || {};
  const segni = [];
  if (conv.contact || conv.contacts?.length) {
    segni.push({ chiave: "contatto", testo: "Contatto", colore: "bg-[#A855F7]", spiega: "Ha lasciato i suoi dati con il modulo" });
  }
  if (a.isHotLead) {
    segni.push({ chiave: "lead", testo: "Lead", colore: "bg-[#F97316]", spiega: "Buone probabilità che diventi un cliente" });
  }
  if (a.sentiment === "negative") {
    segni.push({ chiave: "negativo", testo: "Scontento", colore: "bg-[#EF4444]", spiega: "Il visitatore sembrava insoddisfatto" });
  } else if (a.sentiment === "urgent") {
    segni.push({ chiave: "urgente", testo: "Urgente", colore: "bg-[#F59E0B]", spiega: "Il visitatore aveva fretta" });
  }
  if (a.hasContentGap) {
    segni.push({ chiave: "lacuna", testo: "Risposta mancante", colore: "bg-[#94A3B8]", spiega: "Il bot non aveva le informazioni per rispondere" });
  }
  if (!segni.length) return null;
  return (
    <span className="flex flex-wrap gap-1">
      {segni.map((s) => (
        <span
          key={s.chiave}
          title={s.spiega}
          className="inline-flex items-center gap-1 rounded-full bg-[#F1F5F9] px-2 py-0.5 text-[11px] font-medium text-[#475569]"
        >
          <span className={`h-1.5 w-1.5 rounded-full ${s.colore}`} aria-hidden="true" />
          {s.testo}
        </span>
      ))}
    </span>
  );
}

/** La riga sotto la domanda: chi, quanti messaggi, di cosa. */
function sottotitolo(conv) {
  const parti = [chiE(conv) || (isWhatsApp(conv) ? "WhatsApp" : "Visitatore")];
  if (conv.message_count) parti.push(`${conv.message_count} ${conv.message_count === 1 ? "messaggio" : "messaggi"}`);
  const argomento = ARGOMENTI[conv.analysis?.intent];
  if (argomento) parti.push(argomento);
  return parti.join(" · ");
}

function Elenco({ conversazioni, caricamento, selezionataId, onScegli, paginazione, onPagina, vuoto }) {
  const lista = useRef(null);

  // Frecce su e giu' per passare da una conversazione all'altra.
  const muovi = (e) => {
    if (e.key !== "ArrowDown" && e.key !== "ArrowUp") return;
    const i = conversazioni.findIndex((c) => c.session_id === selezionataId);
    const prossima = conversazioni[e.key === "ArrowDown" ? i + 1 : Math.max(0, i - 1)];
    if (!prossima) return;
    e.preventDefault();
    onScegli(prossima.session_id);
    const voce = [...(lista.current?.querySelectorAll("[data-sessione]") || [])].find(
      (el) => el.dataset.sessione === prossima.session_id
    );
    voce?.focus();
  };

  if (caricamento && !conversazioni.length) {
    return (
      <BrandCard className="space-y-1 p-2">
        {[0, 1, 2, 3, 4].map((i) => (
          <div key={i} className="flex items-start gap-3 p-3">
            <Skeleton className="h-9 w-9 rounded-full" />
            <div className="flex-1 space-y-2">
              <Skeleton className="h-3.5 w-3/4" />
              <Skeleton className="h-3 w-1/2" />
            </div>
          </div>
        ))}
      </BrandCard>
    );
  }
  if (!conversazioni.length) return <BrandCard>{vuoto}</BrandCard>;

  const gruppi = raggruppa(conversazioni, (conv) => gruppoDi(conv.last_activity));

  return (
    <BrandCard className="overflow-hidden">
      <div ref={lista} onKeyDown={muovi}>
        {gruppi.map((gruppo) => (
          <section key={gruppo.nome} aria-label={gruppo.nome}>
            <h2 className="px-4 pb-1 pt-3.5 text-xs font-medium text-[#94A3B8]">{gruppo.nome}</h2>
            <ul className="px-1.5 pb-1.5">
              {gruppo.elementi.map((conv) => (
                <li key={conv.session_id}>
                  <VoceElenco
                    conv={conv}
                    selezionata={conv.session_id === selezionataId}
                    onScegli={() => onScegli(conv.session_id)}
                  />
                </li>
              ))}
            </ul>
          </section>
        ))}
      </div>
      <Paginazione paginazione={paginazione} perPagina={PER_PAGINA} onPagina={onPagina} cosa="Conversazioni" />
    </BrandCard>
  );
}

function VoceElenco({ conv, selezionata, onScegli, esempio = false }) {
  const domanda = pulisci(conv.first_question || conv.preview) || "Conversazione senza messaggi";
  const nuova = conv.status === "new";
  return (
    <button
      type="button"
      data-sessione={conv.session_id}
      onClick={onScegli}
      tabIndex={esempio ? -1 : undefined}
      aria-current={selezionata ? "true" : undefined}
      className={`flex w-full items-start gap-3 rounded-[10px] px-2.5 py-2.5 text-left transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring ${
        selezionata ? "bg-[#F1F5F9]" : "hover:bg-[#F8FAFC]"
      }`}
    >
      <Tondo conv={conv} />
      <span className="min-w-0 flex-1 space-y-1">
        <span className={`line-clamp-2 text-sm leading-5 text-[#020617] ${nuova ? "font-semibold" : "font-medium"}`}>
          {domanda}
          {nuova ? <span className="sr-only"> (da leggere)</span> : null}
        </span>
        <span className="block truncate text-[13px] leading-5 text-[#64748B]">{sottotitolo(conv)}</span>
        <Segni conv={conv} />
      </span>
      {/* Una colonna a parte: il pallino non allunga la riga del titolo. */}
      <Quando valore={conv.last_activity} nuova={nuova} />
    </button>
  );
}

/** L'ora o il giorno e, sotto, il pallino viola se e' da leggere. */
function Quando({ valore, nuova }) {
  return (
    <span className="flex shrink-0 flex-col items-end gap-1.5">
      <span className={`text-xs leading-5 tabular-nums ${nuova ? "font-semibold text-[#020617]" : "text-[#94A3B8]"}`}>
        {quandoBreve(valore)}
      </span>
      {nuova ? <span className="h-2 w-2 rounded-full bg-[#A855F7]" aria-hidden="true" /> : null}
    </span>
  );
}

function ElencoVuoto({ vista, q, inbox, onAzzeraRicerca }) {
  let testo;
  if (q) testo = `Nessuna conversazione contiene «${q}».`;
  else if (vista === "archived") testo = "Nessuna conversazione archiviata. Archivia quelle già lette: restano qui e nell'esportazione.";
  else if (vista === "contatto") testo = "Nessuna conversazione in arrivo ha portato a un contatto.";
  else if (inbox?.archived) testo = "Niente in arrivo: le hai gestite tutte.";
  else testo = "Ancora nessuna conversazione.";
  return (
    <div className="flex flex-col items-center gap-3 px-6 py-12 text-center">
      <p className="max-w-xs text-sm text-[#64748B]">{testo}</p>
      {q ? (
        <button type="button" className={GRIGIO} onClick={onAzzeraRicerca}>
          Cancella la ricerca
        </button>
      ) : null}
    </div>
  );
}

/** Nessuna conversazione ancora: cosa arrivera' qui. */
function Benvenuto({ botId }) {
  const esempio = {
    session_id: "esempio",
    status: "new",
    last_activity: new Date().toISOString(),
    first_question: "Quanto costa rifare un bagno di 6 mq?",
    message_count: 6,
    analysis: { intent: "pricing", isHotLead: true },
    contact: { headline: "Giulia Bianchi" },
  };
  return (
    <BrandCard className="grid grid-cols-[minmax(0,1fr)] gap-8 p-6 sm:p-8 lg:grid-cols-[minmax(0,1fr)_380px] lg:items-center">
      <div className="max-w-lg space-y-3">
        <h2 className="text-lg font-semibold text-[#020617]">Qui trovi ogni chat del tuo bot</h2>
        <p className="text-sm leading-6 text-[#475569]">
          Le domande dei visitatori, le risposte del bot e cosa ne è nato: un contatto, un passaggio a WhatsApp. Ti
          aiutano a capire cosa chiedono i clienti e dove il bot non sa ancora rispondere.
        </p>
        <Link to={`/bots/${botId}/widget`} className={`${GRIGIO} mt-2`}>
          Vai al widget
        </Link>
      </div>
      <div aria-hidden="true">
        <HandNote className="mb-1">così ti arriva una conversazione</HandNote>
        <div className="pointer-events-none rounded-[14px] border border-[#E2E8F0] bg-white p-1.5 shadow-[0_10px_30px_rgba(15,23,42,0.08)]">
          <VoceElenco conv={esempio} selezionata={false} onScegli={() => {}} esempio />
        </div>
      </div>
    </BrandCard>
  );
}

// ---------------------------------------------------------------------------
// La conversazione aperta
// ---------------------------------------------------------------------------

const giornoDi = (valore) => new Date(valore).toDateString();

/**
 * Messaggi e momenti della chat in ordine di tempo: i contatti lasciati si
 * inseriscono dove sono successi.
 */
function cronologia(conv) {
  const voci = (conv.messages || []).map((m, i) => ({ tipo: "messaggio", chiave: `m${i}`, quando: m.timestamp, m }));
  for (const c of conv.contacts || []) {
    voci.push({ tipo: "contatto", chiave: `c${c.id}`, quando: c.created_at, c });
  }
  // Stabile: a parita' di ora il messaggio viene prima del contatto nato da lui.
  return voci
    .map((v, i) => ({ ...v, i }))
    .sort((a, b) => new Date(a.quando) - new Date(b.quando) || a.i - b.i);
}

function Conversazione({ conv, botId, inCorso, onIndietro, onDettagli, onArchivia, onRipristina, onDaLeggere, onCancella }) {
  const archiviata = conv.status === "archived";
  const nome = chiE(conv);
  const contatto = conv.contacts?.at(-1);
  const utente = (conv.messages || []).filter((m) => m.role === "user").length;
  const partenza = conv.messages?.find((m) => m.source_url)?.source_url || conv.referrer;
  const voci = cronologia(conv);

  return (
    // Sullo schermo largo la scheda sta nello schermo: testata e azioni fisse,
    // la chat prende lo spazio che resta e scorre.
    <BrandCard className="pu-carta-in overflow-hidden lg:flex lg:max-h-[calc(100vh-220px)] lg:flex-col">
      {/* La testata in una fascia: chi e' con i suoi segni, sotto in piccolo
          quando, quante domande e da dove; a destra il contatto. */}
      <div className="shrink-0 space-y-3 px-5 py-4">
        <button
          type="button"
          onClick={onIndietro}
          className="-ml-1 inline-flex items-center gap-1.5 rounded-md px-1 text-sm font-medium text-[#64748B] hover:text-[#020617] lg:hidden"
        >
          <ArrowLeft className="h-4 w-4" aria-hidden="true" />
          Tutte le conversazioni
        </button>

        <div className="flex items-center gap-3">
          <Tondo conv={conv} />
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-x-2.5 gap-y-1">
              <h2 className="min-w-0 truncate text-base font-semibold leading-6 text-[#020617]">
                {nome || (isWhatsApp(conv) ? "Chat su WhatsApp" : "Visitatore")}
              </h2>
              <Segni conv={conv} />
              {archiviata ? (
                <span className="inline-flex items-center rounded-full bg-[#F1F5F9] px-2 py-0.5 text-[11px] font-medium text-[#475569]">
                  Archiviata
                </span>
              ) : null}
            </div>
            <p className="mt-0.5 flex min-w-0 flex-wrap items-center gap-x-1.5 text-xs leading-5 text-[#64748B]">
              <span>{quandoEsteso(conv.created_at || conv.messages?.[0]?.timestamp)}</span>
              <span aria-hidden="true">·</span>
              <span>
                {utente} {utente === 1 ? "domanda" : "domande"}
              </span>
              {isWhatsApp(conv) ? (
                <>
                  <span aria-hidden="true">·</span>
                  <span>WhatsApp</span>
                </>
              ) : null}
              {ARGOMENTI[conv.analysis?.intent] ? (
                <>
                  <span aria-hidden="true">·</span>
                  <span>Argomento: {ARGOMENTI[conv.analysis.intent]}</span>
                </>
              ) : null}
              {partenza ? (
                <>
                  <span aria-hidden="true">·</span>
                  <a
                    href={partenza}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex min-w-0 items-center gap-1 hover:text-[#020617]"
                  >
                    Iniziata da <span className="truncate font-medium text-[#334155]">{paginaLeggibile({ page_url: partenza })}</span>
                    <ExternalLink className="h-3 w-3 shrink-0" aria-hidden="true" />
                  </a>
                </>
              ) : null}
            </p>
          </div>
          {contatto ? (
            <Link to={`/bots/${botId}/contatti?contatto=${encodeURIComponent(contatto.id)}`} className={`${SCURO} shrink-0`}>
              Apri il contatto
            </Link>
          ) : null}
        </div>
      </div>

      {/* Sullo schermo largo la chat scorre dentro la scheda, come una chat. */}
      <ol className="space-y-4 border-t border-[#E2E8F0] bg-[#FCFCFD] p-5 sm:p-6 lg:min-h-0 lg:flex-1 lg:overflow-y-auto">
        {voci.map((voce, i) => {
          const giornoNuovo = i > 0 && giornoDi(voce.quando) !== giornoDi(voci[i - 1].quando);
          return (
            <React.Fragment key={voce.chiave}>
              {giornoNuovo ? (
                <li className="text-center text-[11px] font-medium text-[#94A3B8]">{quandoEsteso(voce.quando).split(" alle ")[0]}</li>
              ) : null}
              {voce.tipo === "contatto" ? (
                <MomentoContatto contatto={voce.c} botId={botId} />
              ) : (
                <Messaggio m={voce.m} />
              )}
            </React.Fragment>
          );
        })}
        {!voci.length ? <li className="text-sm text-[#94A3B8]">Nessun messaggio.</li> : null}
      </ol>

      <div className="flex shrink-0 flex-wrap items-center justify-between gap-2 border-t border-[#E2E8F0] px-5 py-3 sm:px-6">
        <div className="-ml-2 flex flex-wrap gap-1">
          <button type="button" onClick={onCancella} className={`${BOTTONE} text-[#B91C1C] hover:bg-red-50`}>
            <Trash2 className="h-3.5 w-3.5" aria-hidden="true" />
            Cancella
          </button>
          <button type="button" onClick={onDettagli} className={`${BOTTONE} text-[#334155] hover:bg-[#F1F5F9]`}>
            <Info className="h-3.5 w-3.5" aria-hidden="true" />
            Dettagli tecnici
          </button>
        </div>
        <div className="flex flex-wrap gap-2">
          {archiviata ? (
            <button type="button" className={GRIGIO} onClick={onRipristina} disabled={inCorso}>
              <ArchiveRestore className="h-3.5 w-3.5" aria-hidden="true" />
              Riporta in arrivo
            </button>
          ) : (
            <>
              <button type="button" className={`${BOTTONE} text-[#334155] hover:bg-[#F1F5F9]`} onClick={onDaLeggere} disabled={inCorso}>
                <CircleDot className="h-3.5 w-3.5" aria-hidden="true" />
                Segna da leggere
              </button>
              <button type="button" className={GRIGIO} onClick={onArchivia} disabled={inCorso}>
                <Archive className="h-3.5 w-3.5" aria-hidden="true" />
                Archivia
              </button>
            </>
          )}
        </div>
      </div>
    </BrandCard>
  );
}

const ora = (valore) => new Date(valore).toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" });

// Cosa ha proposto il bot con quella risposta (salvato dal giro delle integrazioni).
const AZIONI = {
  contact_form: { testo: "Ha proposto il modulo contatti", colore: "bg-[#A855F7]" },
  whatsapp: { testo: "Ha proposto di continuare su WhatsApp", colore: "bg-[#25D366]" },
};

function Messaggio({ m }) {
  const visitatore = m.role === "user";
  const fonti = visitatore ? [] : (m.sources || []).filter((s) => !s.score || s.score >= 0.3);
  const azione = !visitatore && AZIONI[m.action];
  return (
    <li className={`flex flex-col ${visitatore ? "items-end" : "items-start"}`}>
      <span className="mb-1 text-[11px] text-[#94A3B8]">
        {visitatore ? "Visitatore" : "Bot"} · {ora(m.timestamp)}
      </span>
      <div
        className={`max-w-[85%] whitespace-pre-wrap break-words rounded-[14px] px-3.5 py-2.5 text-sm leading-6 ${
          visitatore ? "rounded-br-[4px] bg-[#18181B] text-white" : "rounded-bl-[4px] border border-[#E2E8F0] bg-white text-[#0F172A]"
        }`}
      >
        {pulisci(m.content)}
      </div>
      {azione ? (
        <span className="mt-1.5 inline-flex items-center gap-1.5 rounded-full bg-[#F1F5F9] px-2 py-0.5 text-[11px] font-medium text-[#475569]">
          <span className={`h-1.5 w-1.5 rounded-full ${azione.colore}`} aria-hidden="true" />
          {azione.testo}
        </span>
      ) : null}
      {fonti.length ? (
        <span className="mt-1.5 flex max-w-[85%] flex-wrap gap-x-2 gap-y-1 text-[11px] text-[#94A3B8]">
          Fonti:
          {fonti.map((f, i) =>
            f.url ? (
              <a
                key={i}
                href={f.url}
                target="_blank"
                rel="noopener noreferrer"
                title={f.url}
                className="max-w-[220px] truncate text-[#64748B] underline-offset-2 hover:text-[#020617] hover:underline"
              >
                {f.filename || paginaLeggibile({ page_url: f.url })}
              </a>
            ) : (
              <span key={i} className="max-w-[220px] truncate text-[#64748B]">
                {f.filename}
              </span>
            )
          )}
        </span>
      ) : null}
    </li>
  );
}

/** Il momento in cui il visitatore ha lasciato i suoi dati. */
function MomentoContatto({ contatto, botId }) {
  return (
    <li className="flex items-center gap-3 py-1">
      <span className="h-px flex-1 bg-[#E2E8F0]" aria-hidden="true" />
      <span className="inline-flex items-center gap-1.5 text-xs text-[#475569]">
        <span className="h-1.5 w-1.5 rounded-full bg-[#A855F7]" aria-hidden="true" />
        Ha lasciato i suoi dati alle {ora(contatto.created_at)}
        <Link
          to={`/bots/${botId}/contatti?contatto=${encodeURIComponent(contatto.id)}`}
          className="font-medium text-[#18181B] underline-offset-2 hover:underline"
        >
          Apri il contatto
        </Link>
      </span>
      <span className="h-px flex-1 bg-[#E2E8F0]" aria-hidden="true" />
    </li>
  );
}

function browser(userAgent) {
  if (!userAgent) return "Non disponibile";
  const mobile = /Mobile|Android|iPhone/.test(userAgent) ? " su telefono" : "";
  const nome = /Edg\//.test(userAgent)
    ? "Edge"
    : /Chrome\//.test(userAgent)
      ? "Chrome"
      : /Firefox\//.test(userAgent)
        ? "Firefox"
        : /Safari\//.test(userAgent)
          ? "Safari"
          : "Altro browser";
  return `${nome}${mobile}`;
}

function DettagliTecnici({ conv }) {
  const righe = [
    ["Sessione", <span className="break-all font-mono text-xs">{conv.session_id}</span>],
    ["Iniziata", quandoEsteso(conv.created_at || conv.messages?.[0]?.timestamp)],
    ["Ultima attività", quandoEsteso(conv.last_activity || conv.updated_at)],
    ["Canale", isWhatsApp(conv) ? "WhatsApp" : "Chat del sito"],
    ["Provenienza", conv.referrer ? <span className="break-all">{conv.referrer}</span> : "Accesso diretto"],
    ["Browser", <span title={conv.user_agent || ""}>{browser(conv.user_agent)}</span>],
    ["Indirizzo IP", <span className="font-mono text-xs">{conv.ip_address || "Non disponibile"}</span>],
  ];
  return (
    <dl className="grid grid-cols-[120px_minmax(0,1fr)] gap-x-4 gap-y-2.5 text-sm">
      {righe.map(([etichetta, valore]) => (
        <React.Fragment key={etichetta}>
          <dt className="text-xs font-medium leading-5 text-[#64748B]">{etichetta}</dt>
          <dd className="min-w-0 text-[#0F172A]">{valore}</dd>
        </React.Fragment>
      ))}
    </dl>
  );
}
